import { useCallback, useEffect, useState } from "react";
import { Car, UserPlus, Truck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { canAccessCentroDiurno } from "../../utils/centroDiurnoAccess";
import { getMezzi, createMezzo, getAutisti, createAutista } from "../../utils/centroDiurnoApi";
import PageHeader from "../../components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function CentroDiurnoMezzi() {
  const { token, user, hasPermission, activeStruttura } = useAuth();
  const [mezzi, setMezzi] = useState([]);
  const [autisti, setAutisti] = useState([]);
  const [mezzoForm, setMezzoForm] = useState({ nome: "", targa: "", posti: 8 });
  const [autistaForm, setAutistaForm] = useState({ nome: "", cognome: "", email: "", password: "", telefono: "" });
  const canManage = hasPermission("centro_diurno:manage");

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const [m, a] = await Promise.all([getMezzi(token), getAutisti(token)]);
      setMezzi(m.mezzi || []);
      setAutisti(a.autisti || []);
    } catch (e) {
      toast.error("Errore caricamento");
    }
  }, [token, activeStruttura?.id]);

  useEffect(() => {
    if (canAccessCentroDiurno(user)) load();
  }, [load, user]);

  const saveMezzo = async () => {
    try {
      await createMezzo(token, mezzoForm);
      toast.success("Mezzo aggiunto");
      setMezzoForm({ nome: "", targa: "", posti: 8 });
      load();
    } catch (e) {
      toast.error("Errore salvataggio mezzo");
    }
  };

  const saveAutista = async () => {
    try {
      await createAutista(token, autistaForm);
      toast.success("Autista creato — può accedere da /autista-login");
      setAutistaForm({ nome: "", cognome: "", email: "", password: "", telefono: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione autista");
    }
  };

  if (!canAccessCentroDiurno(user)) {
    return <div className="p-8 text-center text-slate-600">Modulo non attivo</div>;
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto space-y-6">
        <PageHeader
          title="Mezzi e Autisti"
          subtitle="Gestione pulmini e credenziali area autista"
          icon={Truck}
          iconContainerClassName="bg-gradient-to-br from-emerald-500 to-emerald-700"
          iconClassName="text-white"
        />

        <Tabs defaultValue="mezzi">
          <TabsList>
            <TabsTrigger value="mezzi">Mezzi</TabsTrigger>
            <TabsTrigger value="autisti">Autisti</TabsTrigger>
          </TabsList>

          <TabsContent value="mezzi" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Car className="w-4 h-4" /> Pulmini registrati
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {mezzi.map((m) => (
                  <div key={m.id} className="p-3 rounded-lg border bg-white flex justify-between">
                    <span className="font-medium">{m.nome}</span>
                    <span className="text-sm text-slate-500">
                      {m.targa} · {m.posti} posti
                    </span>
                  </div>
                ))}
                {mezzi.length === 0 && <p className="text-sm text-slate-500">Nessun mezzo</p>}
              </CardContent>
            </Card>
            {canManage && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Aggiungi mezzo</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-3 max-w-md">
                  <div>
                    <Label>Nome</Label>
                    <Input value={mezzoForm.nome} onChange={(e) => setMezzoForm({ ...mezzoForm, nome: e.target.value })} placeholder="Pulmino 1" />
                  </div>
                  <div>
                    <Label>Targa</Label>
                    <Input value={mezzoForm.targa} onChange={(e) => setMezzoForm({ ...mezzoForm, targa: e.target.value })} />
                  </div>
                  <div>
                    <Label>Posti</Label>
                    <Input type="number" value={mezzoForm.posti} onChange={(e) => setMezzoForm({ ...mezzoForm, posti: Number(e.target.value) })} />
                  </div>
                  <Button className="bg-[#4CAF50] hover:bg-[#43A047] w-fit" onClick={saveMezzo}>
                    Salva mezzo
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="autisti" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Autisti registrati</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {autisti.map((a) => (
                  <div key={a.id} className="p-3 rounded-lg border bg-white">
                    <p className="font-medium">
                      {a.nome} {a.cognome}
                    </p>
                    <p className="text-xs text-slate-500">{a.email} · {a.telefono}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
            {canManage && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <UserPlus className="w-4 h-4" /> Nuovo autista
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid gap-3 max-w-md">
                  <Input placeholder="Nome" value={autistaForm.nome} onChange={(e) => setAutistaForm({ ...autistaForm, nome: e.target.value })} />
                  <Input placeholder="Cognome" value={autistaForm.cognome} onChange={(e) => setAutistaForm({ ...autistaForm, cognome: e.target.value })} />
                  <Input type="email" placeholder="Email login" value={autistaForm.email} onChange={(e) => setAutistaForm({ ...autistaForm, email: e.target.value })} />
                  <Input type="password" placeholder="Password" value={autistaForm.password} onChange={(e) => setAutistaForm({ ...autistaForm, password: e.target.value })} />
                  <Input placeholder="Telefono" value={autistaForm.telefono} onChange={(e) => setAutistaForm({ ...autistaForm, telefono: e.target.value })} />
                  <Button className="bg-[#4CAF50] hover:bg-[#43A047] w-fit" onClick={saveAutista}>
                    Crea autista
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
